import { Link } from "react-router-dom";
import { Wordmark } from "../components/Wordmark";
import { ArrowLeft, FileText } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-serif text-2xl text-charcoal">{title}</h2>
    <div className="mt-3 space-y-3 text-charcoal/85 leading-relaxed">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <div className="min-h-screen grain bg-cream" data-testid="terms-page">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-home-link">
            <img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" />
            <Wordmark size="text-lg sm:text-xl" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold">
          <FileText size={14} /> The essentials
        </span>
        <h1 className="font-serif text-4xl text-charcoal mt-3">Terms of Service</h1>
        <p className="text-subtle mt-2">Last updated: June 2026</p>

        <p className="mt-6 text-charcoal/85 leading-relaxed">
          These terms govern your use of Write My Story, a memoir-writing service created by Juliet Sullivan.
          By creating an account or using the service, you agree to these terms. If you do not agree, please
          do not use the service.
        </p>

        <Section title="The service">
          <p>Write My Story helps you record memories by voice or text and uses artificial intelligence to help organise them into written prose, chapters and a book. You can also invite family and friends to contribute memories, which you review before they join your story.</p>
        </Section>

        <Section title="Your account">
          <p>You are responsible for keeping your login details secure and for activity that happens under your account. Please provide accurate information and let us know promptly of any unauthorised use. You must be at least 16 years old to use the service.</p>
        </Section>

        <Section title="Free trial, subscriptions and billing">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>New accounts include a <strong>7-day free trial</strong> with no card required.</li>
            <li>To continue after the trial, you choose a paid plan. Payments are processed securely by Stripe.</li>
            <li>Prices are shown in GBP. If your card is in another currency, the amount is converted at checkout or by your bank.</li>
            <li>Recurring plans renew automatically until cancelled. You can cancel at any time; access continues until the end of your current paid period. One-off lifetime purchases are a single payment.</li>
            <li>Except where required by law, payments are non-refundable.</li>
          </ul>
        </Section>

        <Section title="Your content and ownership">
          <p>Your memories, recordings, photos and the memoir you create are <strong>yours</strong>. You grant us a limited licence to store and process your content solely to provide the service to you — for example, to transcribe recordings and generate your drafts using our AI providers. We do not claim ownership of your story and we do not use it to advertise.</p>
        </Section>

        <Section title="Family contributions">
          <p>When you share a contribution link, anyone with the link can submit a memory about you. You are responsible for who you share the link with. Contributions remain pending until you approve them, and you can decline or remove them.</p>
        </Section>

        <Section title="AI-generated content">
          <p>The service uses AI to help draft and organise your memoir. AI output may contain mistakes or misinterpretations, so please review and edit your drafts. The finished memoir reflects the content you provide and approve.</p>
        </Section>

        <Section title="Acceptable use">
          <p>Please use the service lawfully. Do not upload content that is unlawful, infringes others' rights, or that you do not have permission to share, and do not attempt to disrupt or misuse the service.</p>
        </Section>

        <Section title="Availability">
          <p>We work hard to keep the service running, but it is provided "as is" without warranties of uninterrupted or error-free operation. We may update, suspend or discontinue features from time to time.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>To the extent permitted by law, we are not liable for indirect or consequential losses, or for loss of data beyond our reasonable control. Nothing in these terms limits liability that cannot be limited by law. We recommend keeping your own copies of anything important, and the app lets you export your book.</p>
        </Section>

        <Section title="Ending your use">
          <p>You can stop using the service and delete your account at any time from Settings. We may suspend or end access if these terms are breached.</p>
        </Section>

        <Section title="Governing law">
          <p>These terms are governed by the laws of England and Wales, and disputes are subject to the courts of England and Wales.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about these terms? Email <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a>.</p>
          <p>Write My Story is a memoir service by author Juliet Sullivan.<br />
          275 New North Road, London, N1 7AA, United Kingdom.</p>
        </Section>

        <div className="mt-10 pt-6 border-t border-line text-sm text-subtle">
          See also our <Link to="/privacy" className="text-teal underline">Privacy Policy</Link>.
        </div>
      </main>
    </div>
  );
}
